import React, { useState } from 'react';
import { Icons } from '../../utils/icons';
import { authService } from '../../services/authService';

const ESTADO_CFG = {
  aceptado:  { label: 'Aceptado',  bg: 'bg-green-100', text: 'text-green-700' },
  rechazado: { label: 'Rechazado', bg: 'bg-red-100',   text: 'text-red-700'   },
  pendiente: { label: 'Pendiente', bg: 'bg-gray-100',  text: 'text-gray-500'  },
};

const TIPO_CFG = {
  tarea:  { label: 'Tarea',  bg: 'bg-amber-100', text: 'text-amber-700' },
  visita: { label: 'Visita', bg: 'bg-blue-100',  text: 'text-blue-700'  },
  reunion:{ label: 'Reunión',bg: 'bg-red-100',   text: 'text-red-700'   },
};

function fmt(fecha) {
  if (!fecha) return '';
  const [y, m, d] = fecha.split('-');
  return `${d}/${m}/${y}`;
}

function normTel(tel) {
  if (!tel) return '';
  let t = tel.replace(/\D/g, '');
  if (!t.startsWith('34') && t.length === 9) t = '34' + t;
  return t;
}

export default function EventoDetalle({ ocurrencia, usuario, onClose, onEditar }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const esCreador = ocurrencia.creadorId?.toLowerCase() === usuario.email?.toLowerCase();
  const puedeGestionar = esCreador || usuario.role === 'admin';
  const miParticipacion = (ocurrencia.participantes || []).find(p => p.userId?.toLowerCase() === usuario.email?.toLowerCase());

  async function responder(respuesta) {
    setSaving(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/responderInvitacion', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ eventoId: ocurrencia.eventoId, fechaOriginal: ocurrencia.fechaOriginal, userId: usuario.email, respuesta }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const horaStr = ocurrencia.todoElDia ? 'Todo el día' : `${ocurrencia.horaInicio || ''}${ocurrencia.horaFin ? ` – ${ocurrencia.horaFin}` : ''}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIPO_CFG[ocurrencia.tipo]?.bg || TIPO_CFG.reunion.bg} ${TIPO_CFG[ocurrencia.tipo]?.text || TIPO_CFG.reunion.text}`}>
              {TIPO_CFG[ocurrencia.tipo]?.label || 'Reunión'}
            </span>
            <h3 className="font-bold text-gray-900 mt-1">{ocurrencia.titulo}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="space-y-1.5 text-sm">
            <p className="text-gray-700">📅 {fmt(ocurrencia.fechaInicio)}{ocurrencia.fechaFin && ocurrencia.fechaFin !== ocurrencia.fechaInicio ? ` – ${fmt(ocurrencia.fechaFin)}` : ''}</p>
            {(ocurrencia.tipo !== 'tarea' || ocurrencia.horaInicio) && <p className="text-gray-700">🕐 {horaStr}</p>}
            {ocurrencia.ubicacion && <p className="text-gray-700">📍 {ocurrencia.ubicacion}</p>}
            <p className="text-gray-400 text-xs">Creado por {ocurrencia.creadorNombre}</p>
          </div>

          {ocurrencia.tipo === 'visita' && ocurrencia.clienteNombre && (
            <div className="bg-blue-50 rounded-xl p-3 text-sm space-y-2">
              <div>
                <p className="text-xs font-bold text-blue-500 uppercase tracking-widest mb-1">Cliente</p>
                <p className="text-blue-800 font-bold">{ocurrencia.clienteNombre}</p>
                {ocurrencia.clienteTelefono && <p className="text-blue-700">{ocurrencia.clienteTelefono}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {ocurrencia.clienteTelefono && normTel(ocurrencia.clienteTelefono) && (
                  <a href={`https://wa.me/${normTel(ocurrencia.clienteTelefono)}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors">
                    <Icons.MessageCircle className="w-3.5 h-3.5" />WhatsApp
                  </a>
                )}
                {ocurrencia.ubicacion && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ocurrencia.ubicacion)}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors">
                    <Icons.MapPin className="w-3.5 h-3.5" />Google Maps
                  </a>
                )}
              </div>
            </div>
          )}

          {ocurrencia.descripcion && <p className="text-sm text-gray-600 whitespace-pre-wrap">{ocurrencia.descripcion}</p>}

          {(ocurrencia.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {ocurrencia.tags.map(t => (
                <span key={t} className="text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">#{t}</span>
              ))}
            </div>
          )}

          {(ocurrencia.participantes || []).length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Participantes</p>
              <div className="space-y-1.5">
                {ocurrencia.participantes.map(p => (
                  <div key={p.userId} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{p.nombre}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ESTADO_CFG[p.estado]?.bg} ${ESTADO_CFG[p.estado]?.text}`}>
                      {ESTADO_CFG[p.estado]?.label || p.estado}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            {puedeGestionar && (
              <button onClick={onEditar}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                <Icons.Edit className="w-4 h-4" />Editar
              </button>
            )}
          </div>
          {miParticipacion && !esCreador && (
            <div className="flex gap-2">
              <button onClick={() => responder('rechazado')} disabled={saving}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 ${miParticipacion.estado === 'rechazado' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>
                <Icons.X className="w-4 h-4" />Rechazar
              </button>
              <button onClick={() => responder('aceptado')} disabled={saving}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 ${miParticipacion.estado === 'aceptado' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                <Icons.Check className="w-4 h-4" />Aceptar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
